import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import { Tooltip } from 'antd';
import { PlusOutlined, InfoCircleOutlined } from '@ant-design/icons';
import Button from 'components/new-ui/base/button/Button';
import { StyledIconWrapper } from 'components/new-ui/base/page-header/styles';
import { H2 } from 'components/new-ui/base/typography/styles';
import Notification from 'components/ui/base/notification/Notification';
import { hasPermissions } from 'config/roles/hasPermissions';
import { DEFAULT_INTERNAL_ERROR_MESSAGE } from 'core/utils/constants';
import useManifests from 'hooks/useManifests';
import { arrivalErrorMessage } from 'hooks/useIcs2ArrivalNotification';
import useSession from 'hooks/useSession';
import { ICS2ArrivalView } from 'store/declarations/eu/ics2/arrival/ics2-arrival-notification-declaration';
import { getErrorMessage } from 'utils/getErrorMessage';
import ArrivalNotificationsTable from 'views/ics2-arrival-notifications/ArrivalNotificationsTable';
import { createArrivalForManifest, isManifestArrivalEligible } from 'views/ics2-arrival-notifications/manifestArrival';
import { listIcs2Arrivals } from 'store/declarations/eu/ics2/arrival/client';

const ArrivalNotificationIcon = () => (
    <StyledIconWrapper style={{ width: 24, height: 24, borderRadius: 6 }}>
        <InfoCircleOutlined style={{ fontSize: 14 }} />
    </StyledIconWrapper>
);

type Props = {
    manifestId?: string;
};

const ManifestArrivalNotificationSection = ({ manifestId }: Props) => {
    const navigate = useNavigate();
    const { userInfo } = useSession();
    const { manifest } = useManifests();

    const [arrivals, setArrivals] = useState<ICS2ArrivalView[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [isEligible, setIsEligible] = useState(false);

    const canCreate = hasPermissions(userInfo, 'declarations', 'CREATE');

    useEffect(() => {
        if (!manifestId) return;

        const controller = new AbortController();

        setIsLoading(true);
        listIcs2Arrivals({
            manifestId,
            signal: controller.signal,
        })
            .then((res) => setArrivals(res.list))
            .catch((error) => {
                if (axios.isCancel(error) || controller.signal.aborted) return;

                Notification({
                    type: 'error',
                    placement: 'top',
                    messageTitle: 'Could not load the arrival notification.',
                    description: getErrorMessage(error) || DEFAULT_INTERNAL_ERROR_MESSAGE,
                });
            })
            .finally(() => {
                if (!controller.signal.aborted) setIsLoading(false);
            });

        return () => controller.abort();
    }, [manifestId]);

    // The store may still hold the previously opened manifest, so the check waits for this one.
    const referenceId = manifest?.id === manifestId ? manifest?.referenceId : undefined;

    useEffect(() => {
        setIsEligible(false);
        if (!manifestId || !referenceId) return;

        let cancelled = false;
        isManifestArrivalEligible({ id: manifestId, referenceId })
            .then((eligible) => !cancelled && setIsEligible(eligible))
            .catch(() => {});

        return () => {
            cancelled = true;
        };
    }, [manifestId, referenceId]);

    const createArrival = useCallback(async () => {
        if (!manifestId) return;

        setIsCreating(true);
        try {
            const arrival = await createArrivalForManifest(manifestId);

            navigate(`/arrival-notifications/${arrival.id}`);
        } catch (error) {
            Notification({
                placement: 'top',
                type: 'error',
                messageTitle: 'Could not create the arrival notification.',
                description: arrivalErrorMessage(error) || DEFAULT_INTERNAL_ERROR_MESSAGE,
            });
        } finally {
            setIsCreating(false);
        }
    }, [manifestId, navigate]);

    return (
        <div style={{ padding: 24, backgroundColor: 'var(--gray-1)', borderRadius: 6, marginTop: 24 }}>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 8,
                    marginBottom: 16,
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <ArrivalNotificationIcon />
                    <H2>Arrival Notification</H2>
                </div>

                {/* A manifest has at most one arrival, so the offer to create one goes away with it. */}
                {canCreate && !isLoading && !arrivals.length && (
                    <Tooltip
                        title={
                            isEligible
                                ? undefined
                                : 'Available once every master ENS of this manifest is accepted by customs.'
                        }
                    >
                        <span>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                loading={isCreating}
                                disabled={!isEligible}
                                onClick={createArrival}
                                data-testid="create-arrival-notification-button"
                            >
                                Create Arrival Notification
                            </Button>
                        </span>
                    </Tooltip>
                )}
            </div>

            <ArrivalNotificationsTable
                showManifestId={false}
                loading={isLoading}
                dataSource={arrivals}
                pagination={false}
                scroll={{ x: 'max-content' }}
                locale={{ emptyText: 'No arrival notification has been raised for this manifest yet.' }}
            />
        </div>
    );
};

export default ManifestArrivalNotificationSection;
